import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase, SUPABASE_FUNCTIONS_URL } from '../lib/supabase';
import type { Booking, BookingStats, FilterType, SortType, PriceCheck } from '../lib/types';
import { isFlightPast } from '../lib/utils/booking';
import { Sentry } from '../lib/sentry';


/**
 * Central booking state manager. Fetches bookings from the get-bookings edge
 * function and derives stats, filtered views, and computed partitions locally.
 *
 * Key patterns:
 * - A 30s tick interval forces re-renders so relative timestamps ("2m ago")
 *   stay current without each component managing its own timer.
 * - watchingBookings/historyBookings are computed inline (not state) to
 *   stay in sync with allBookings without extra effects.
 */
export function useBookings() {
  const [allBookings, setAllBookings] = useState<Booking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<Booking[]>([]);
  const [stats, setStats] = useState<BookingStats>({
    total_bookings: 0,
    active_bookings: 0,
    bookings_with_cheaper_options: 0,
    total_potential_savings: 0,
    total_cash_savings: 0,
    total_points_savings: 0,
    cash_bookings: 0,
    award_bookings: 0,
    days_monitoring: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [archivedCount, setArchivedCount] = useState(0);
  // Tick for forcing re-renders on time-based displays
  const [, setTick] = useState(0);

  // Load bookings from API
  const loadBookings = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/get-bookings`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Server error (${response.status})`);
      }

      let result;
      try {
        result = await response.json();
      } catch {
        throw new Error('Invalid response from server');
      }

      if (result.success && result.bookings) {
        setAllBookings(result.bookings);
        setFilteredBookings(result.bookings);

        // --- Derive stats from bookings in a single pass ---
        const bookings = result.bookings;
        let activeCount = 0;
        let cheaperCount = 0;
        let totalSavings = 0;
        let cashSavings = 0;
        let pointsSavings = 0;
        let cashCount = 0;
        let awardCount = 0;
        let oldestCreatedAt = Date.now();

        for (const b of bookings as Booking[]) {
          if (b.is_active) activeCount++;
          if (b.has_cheaper_option) {
            cheaperCount++;
            const s = b.potential_savings || 0;
            totalSavings += s;
            if (b.is_award_booking) pointsSavings += s;
            else cashSavings += s;
          }
          if (b.is_award_booking) awardCount++;
          else cashCount++;
          const created = new Date(b.created_at).getTime();
          if (created < oldestCreatedAt) oldestCreatedAt = created;
        }

        const daysMonitoring = Math.max(1, Math.floor((Date.now() - oldestCreatedAt) / (1000 * 60 * 60 * 24)));

        setStats({
          total_bookings: bookings.length,
          active_bookings: activeCount,
          bookings_with_cheaper_options: cheaperCount,
          total_potential_savings: totalSavings,
          total_cash_savings: cashSavings,
          total_points_savings: pointsSavings,
          cash_bookings: cashCount,
          award_bookings: awardCount,
          days_monitoring: daysMonitoring,
        });
      } else {
        setAllBookings([]);
        setFilteredBookings([]);
      }
    } catch (err) {
      Sentry.captureException(err);
      setError(err instanceof Error ? err.message : 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, []);

  // Apply filters and sorting
  const applyFilters = useCallback((
    filter: FilterType,
    searchTerm: string,
    sortBy: SortType
  ) => {
    let filtered = [...allBookings];

    // Apply search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(booking => {
        const searchStr = `${booking.pnr} ${booking.airline_code} ${
          booking.segments?.map(s => `${s.origin} ${s.destination}`).join(' ')
        }`.toLowerCase();
        return searchStr.includes(search);
      });
    }

    // Apply filter
    switch (filter) {
      case 'cheaper':
        filtered = filtered.filter(b => b.has_cheaper_option);
        break;
      case 'active':
        filtered = filtered.filter(b => b.is_active);
        break;
      case 'award':
        filtered = filtered.filter(b => b.is_award_booking);
        break;
      case 'cash':
        filtered = filtered.filter(b => !b.is_award_booking);
        break;
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.booking_date).getTime() - new Date(a.booking_date).getTime();
        case 'date-asc':
          return new Date(a.booking_date).getTime() - new Date(b.booking_date).getTime();
        case 'savings-desc':
          return (b.potential_savings || 0) - (a.potential_savings || 0);
        case 'price-desc':
          return b.total_price_paid - a.total_price_paid;
        case 'price-asc':
          return a.total_price_paid - b.total_price_paid;
        default:
          return 0;
      }
    });

    setFilteredBookings(filtered);
  }, [allBookings]);

  // Get price history for a booking
  const getPriceHistory = useCallback(async (bookingId: string): Promise<PriceCheck[]> => {
    const { data, error } = await supabase
      .from('price_checks')
      .select('*')
      .eq('booking_id', bookingId)
      .order('checked_at', { ascending: true });

    if (error) {
      console.error('Error fetching price history:', error);
      Sentry.captureException(error);
      return [];
    }

    return data || [];
  }, []);

  /**
   * Triggers an on-demand price check via the check-prices edge function.
   * Returns success/failure -- the caller is responsible for reloading
   * bookings afterward to reflect any new price data.
   */
  const checkPrice = useCallback(async (bookingId: string): Promise<boolean> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/check-prices`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ booking_id: bookingId }),
      });

      const result = await response.json();
      return result.success || false;
    } catch (err) {
      console.error('Error checking price:', err);
      Sentry.captureException(err);
      return false;
    }
  }, []);

  // Clear archived count notification
  const clearArchivedCount = useCallback(() => {
    setArchivedCount(0);
  }, []);

  // Stop watching a specific booking
  const stopWatching = useCallback(async (bookingId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('tracked_bookings')
        .update({
          is_active: false,
          booking_status: 'stopped',
          updated_at: new Date().toISOString(),
        })
        .eq('id', bookingId);

      if (error) throw error;

      // Reload bookings to reflect changes
      await loadBookings();
      return true;
    } catch (err) {
      console.error('Error stopping booking watch:', err);
      Sentry.captureException(err);
      return false;
    }
  }, [loadBookings]);

  // Permanently delete a booking and all related data
  const deleteBooking = useCallback(async (bookingId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('tracked_bookings')
        .delete()
        .eq('id', bookingId);

      if (error) throw error;

      await loadBookings();
      return true;
    } catch (err) {
      console.error('Error deleting booking:', err);
      Sentry.captureException(err);
      return false;
    }
  }, [loadBookings]);

  // Load bookings on mount
  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  // Live update tick for relative timestamps (every 30 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Computed: watching bookings (active, not departed)
  // Recently rebooked bookings sort to top for 7 days (victory lap)
  const VICTORY_LAP_MS = 7 * 24 * 60 * 60 * 1000;
  const watchingBookings = useMemo(
    () => {
      const active = allBookings.filter(b => b.is_active && !isFlightPast(b.segments));
      return active.sort((a, b) => {
        const aRebooked = a.booking_status === 'rebooked' && Date.now() - new Date(a.updated_at).getTime() < VICTORY_LAP_MS;
        const bRebooked = b.booking_status === 'rebooked' && Date.now() - new Date(b.updated_at).getTime() < VICTORY_LAP_MS;
        if (aRebooked && !bRebooked) return -1;
        if (!aRebooked && bRebooked) return 1;
        return new Date(b.booking_date).getTime() - new Date(a.booking_date).getTime();
      });
    },
    [allBookings],
  );

  // Computed: history bookings (inactive or departed)
  const historyBookings = useMemo(
    () => allBookings.filter(b => !b.is_active || isFlightPast(b.segments)),
    [allBookings],
  );

  // Computed: most recent last_checked_at across all bookings
  const lastCheckedAt = useMemo(
    () => allBookings.reduce<string | null>((latest, booking) => {
      if (!booking.last_checked_at) return latest;
      if (!latest) return booking.last_checked_at;
      return new Date(booking.last_checked_at) > new Date(latest)
        ? booking.last_checked_at
        : latest;
    }, null),
    [allBookings],
  );

  // Batch-fetch price histories for sparklines (one query instead of N)
  const [priceHistoryMap, setPriceHistoryMap] = useState<Record<string, number[]>>({});

  const loadPriceHistories = useCallback(async (bookingIds: string[]) => {
    if (bookingIds.length === 0) return;

    try {
      const { data, error } = await supabase
        .from('price_checks')
        .select('booking_id, current_price, checked_at')
        .in('booking_id', bookingIds)
        .not('current_price', 'is', null)
        .order('checked_at', { ascending: true });

      if (error || !data) return;

      // Group prices by booking_id
      const map: Record<string, number[]> = {};
      for (const row of data) {
        if (row.current_price == null) continue;
        if (!map[row.booking_id]) map[row.booking_id] = [];
        map[row.booking_id].push(row.current_price);
      }
      setPriceHistoryMap(map);
    } catch {
      // Sparklines are decorative — fail silently
    }
  }, []);

  // Fetch price histories when bookings change
  useEffect(() => {
    const ids = allBookings
      .filter(b => !isFlightPast(b.segments))
      .map(b => b.id);
    loadPriceHistories(ids);
  }, [allBookings, loadPriceHistories]);

  // Computed: total value of active bookings under watch (portfolio framing)
  const portfolioValue = useMemo(
    () => watchingBookings.reduce((sum, b) => sum + (b.total_price_paid || 0), 0),
    [watchingBookings],
  );

  // Computed: lifetime GROSS savings from savings_events (what the price dropped by)
  const [lifetimeSavings, setLifetimeSavings] = useState(0);

  const loadLifetimeSavings = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('savings_events')
        .select('savings_amount')

      if (error || !data) return;
      const total = data.reduce((sum: number, row: { savings_amount: number }) => sum + (row.savings_amount || 0), 0);
      setLifetimeSavings(Math.round(total));
    } catch {
      // Non-critical — fallback to 0
    }
  }, []);

  // Fetch lifetime savings when bookings change
  useEffect(() => {
    loadLifetimeSavings();
  }, [allBookings, loadLifetimeSavings]);

  return {
    allBookings,
    filteredBookings,
    watchingBookings,
    historyBookings,
    stats,
    loading,
    error,
    archivedCount,
    lastCheckedAt,
    portfolioValue,
    lifetimeSavings,
    priceHistoryMap,
    loadBookings,
    applyFilters,
    getPriceHistory,
    checkPrice,
    clearArchivedCount,
    stopWatching,
    deleteBooking,
  };
}
